import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { createEditor, Editor, Transforms, Element as SlateElement, Text, Range } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import styles from './SlateRichTextEditor.module.css';

// Define list types for slate
const LIST_TYPES = ['bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

// Custom function to handle pasting
const withHtml = editor => {
  const { insertData } = editor;

  editor.insertData = data => {
    const html = data.getData('text/html');
    
    if (html) {
      // Create a temporary DOM element to parse the HTML
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      
      // Process the HTML to extract formatting
      const fragment = deserializeHtml(parsed.body);
      
      // Insert the formatted fragment
      if (fragment.length > 0) {
        Editor.insertFragment(editor, fragment);
        return;
      }
    }
    
    // Fall back to the original behavior
    insertData(data);
  };

  return editor;
};

// Convert HTML to Slate nodes
const deserializeHtml = el => {
  if (el.nodeType === 3) {
    // Text node
    return { text: el.textContent };
  } else if (el.nodeType !== 1) {
    // Not an element, skip it
    return null;
  }

  const children = Array.from(el.childNodes)
    .map(deserializeHtml)
    .flat()
    .filter(node => node !== null);

  // Handle specific HTML elements
  switch (el.nodeName.toLowerCase()) {
    case 'body':
      return children;
    case 'br':
      return { text: '\n' };
    case 'p':
      return { type: 'paragraph', children: children.length ? children : [{ text: '' }] };
    case 'strong':
    case 'b':
      return children.map(child => ({ ...child, bold: true }));
    case 'em':
    case 'i':
      return children.map(child => ({ ...child, italic: true }));
    case 'u':
      return children.map(child => ({ ...child, underline: true }));
    case 's':
    case 'strike':
    case 'del':
      return children.map(child => ({ ...child, strikethrough: true }));
    case 'ul':
      return { type: 'bulleted-list', children: children.length ? children : [{ type: 'list-item', children: [{ text: '' }] }] };
    case 'li':
      return { type: 'list-item', children: children.length ? children : [{ text: '' }] };
    default:
      // For all other elements, just return the children
      return children;
  }
};

const SlateRichTextEditor = ({
  value,
  onChange,
  placeholder,
  name,
  onBlur,
  className,
  style,
  error
}) => {
  // Initialize default value if none provided
  const initialValue = useMemo(() => {
    // If value is not provided, return default empty paragraph
    if (!value) {
      return [{ type: 'paragraph', children: [{ text: '' }] }];
    }
    
    // If value is already an array (proper Slate format), use it
    if (Array.isArray(value)) {
      return value;
    }
    
    // If value is a string that looks like HTML
    if (typeof value === 'string' && (value.includes('<') || value.includes('&lt;'))) {
      try {
        // Handle HTML entities if they're escaped
        let htmlContent = value;
        if (htmlContent.includes('&lt;')) {
          htmlContent = htmlContent
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
        }
        
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        const bodyContent = doc.body;
        
        // Convert DOM to Slate format
        if (bodyContent) {
          const slateNodes = [];
          
          Array.from(bodyContent.childNodes).forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const nodeName = node.nodeName.toLowerCase();
              
              if (nodeName === 'p') {
                slateNodes.push({
                  type: 'paragraph',
                  children: [{ text: node.textContent || '' }]
                });
              } else if (nodeName === 'ul') {
                const listNode = { type: 'bulleted-list', children: [] };
                
                Array.from(node.querySelectorAll('li')).forEach(li => {
                  listNode.children.push({
                    type: 'list-item',
                    children: [{ text: li.textContent || '' }]
                  });
                });
                
                slateNodes.push(listNode);
              } else {
                slateNodes.push({
                  type: 'paragraph',
                  children: [{ text: node.textContent || '' }]
                });
              }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
              slateNodes.push({
                type: 'paragraph',
                children: [{ text: node.textContent }]
              });
            }
          });
          
          // If we got valid nodes, return them
          if (slateNodes.length > 0) {
            return slateNodes;
          }
        }
      } catch (error) {
        console.error('Error converting HTML to Slate format:', error);
        // Fall through to default case below
      }
    }
    
    // For plain text or if HTML parsing fails
    return [{ type: 'paragraph', children: [{ text: typeof value === 'string' ? value : '' }] }];
  }, [value]);

  // Check if the editor content is empty
  const checkIsEmpty = (val) => {
    if (!val || !val.length) return true;
    return (
      val.length === 1 && 
      val[0].children.length === 1 && 
      val[0].children[0].text === ''
    );
  };

  // Track if the editor is empty for proper placeholder display
  const [isEmpty, setIsEmpty] = useState(() => checkIsEmpty(initialValue));

  // Create a Slate editor object that won't change across renders
  const editor = useMemo(() => withHtml(withHistory(withReact(createEditor()))), []);

  // Define a rendering function based on the element's type property
  const renderElement = useCallback(props => {
    const { attributes, children, element } = props;
    
    switch (element.type) {
      case 'bulleted-list':
        return (
          <ul 
            {...attributes} 
            className={styles.bulletedList}
          >
            {children}
          </ul>
        );
      case 'list-item':
        return (
          <li 
            {...attributes} 
            className={styles.listItem}
          >
            {children}
          </li>
        );
      default:
        return <p {...attributes}>{children}</p>;
    }
  }, []);

  // Define a rendering function for text formatting
  const renderLeaf = useCallback(props => {
    const { attributes, children, leaf } = props;

    let formattedChildren = children;

    if (leaf.bold) {
      formattedChildren = <strong>{formattedChildren}</strong>;
    }

    if (leaf.italic) {
      formattedChildren = <em>{formattedChildren}</em>;
    }

    if (leaf.underline) {
      formattedChildren = <u>{formattedChildren}</u>;
    }

    if (leaf.strikethrough) {
      formattedChildren = <s>{formattedChildren}</s>;
    }

    return <span {...attributes}>{formattedChildren}</span>;
  }, []);

  // Handle value changes and format for parent component
  const handleChange = newValue => {
    // Check if the editor is empty for placeholder styling
    setIsEmpty(checkIsEmpty(newValue));
    
    onChange({
      target: { name, value: newValue }
    });
  };

  // Handle blur event
  const handleBlur = () => {
    if (onBlur) {
      onBlur({ target: { name } });
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = event => {
    // Don't handle keyboard shortcuts for empty selections
    if (!editor.selection) return;

    // Handle keyboard shortcuts for formatting
    if (event.ctrlKey || event.metaKey) {  // Add support for Command key on Mac
      switch (event.key.toLowerCase()) {  // Normalize key to lowercase for better cross-browser support
        case 'b': {
          event.preventDefault();
          toggleMark(editor, 'bold');
          break;
        }
        case 'i': {
          event.preventDefault();
          toggleMark(editor, 'italic');
          break;
        }
        case 'u': {
          event.preventDefault();
          toggleMark(editor, 'underline');
          break;
        }
        case '-': 
        case 's': { // Support both Ctrl+S and Ctrl+- for strikethrough
          event.preventDefault();
          toggleMark(editor, 'strikethrough');
          break;
        }
        case '*':
        case '8': {
          // Ctrl+8 or Ctrl+* for bullet list
          if (event.key === '8' && event.shiftKey) {
            event.preventDefault();
            toggleBlock(editor, 'bulleted-list');
          } else if (event.key === '*') {
            event.preventDefault();
            toggleBlock(editor, 'bulleted-list');
          }
          break;
        }
        default:
          break;
      }
    }
  };

  // Determine container classes based on error state and emptiness
  const containerClasses = `${styles.editorContainer} ${className || ''} ${error ? styles.hasError : ''} ${isEmpty ? 'slate-empty' : ''}`;

  return (
    <div className={containerClasses} style={style}>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onChange={handleChange}
      >
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.toolbarButton}
            onMouseDown={e => {
              e.preventDefault();
              toggleMark(editor, 'bold');
            }}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            className={styles.toolbarButton}
            onMouseDown={e => {
              e.preventDefault();
              toggleMark(editor, 'italic');
            }}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button" 
            className={styles.toolbarButton}
            onMouseDown={e => {
              e.preventDefault();
              toggleMark(editor, 'underline');
            }}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <button
            type="button"
            className={styles.toolbarButton}
            onMouseDown={e => {
              e.preventDefault();
              toggleMark(editor, 'strikethrough');
            }}
            title="Strikethrough (Ctrl+S)"
          >
            <s>S</s>
          </button>
          <button
            type="button"
            className={styles.toolbarButton}
            onMouseDown={e => {
              e.preventDefault();
              toggleBlock(editor, 'bulleted-list');
            }}
            title="Bullet List (Ctrl+Shift+8)"
          >
            • List
          </button>
          <div className={styles.shortcutsInfo}>
            Shortcuts: Ctrl+B (Bold), Ctrl+I (Italic), Ctrl+U (Underline), Ctrl+S (Strikethrough)
          </div>
        </div>
        <div className={styles.editorWrapper}>
          {isEmpty && <div className={styles.placeholder}>{placeholder || 'Enter text...'}</div>}
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder=""
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={styles.editor}
            spellCheck={false}
            autoFocus={false}
            style={{ outline: 'none' }}
            data-slate-editor={true}
          />
        </div>
      </Slate>
      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  );
};

// Helper to check if mark is active
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// Toggle a mark on/off
const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// Check if block type is active
const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n => 
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );

  return !!match;
};

// Toggle block type
const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type),
    split: true,
  });
  
  const newProperties = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  };
  
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export default SlateRichTextEditor; 